import {
  Children,
  ElementRef,
  FC,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
} from 'react';

import { BsCheck, BsDash } from 'react-icons/bs';
import { cn } from 'utils/helpers';

import { PROPS } from '../Input/Input';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  iconType?: 'check' | 'minus';
  fill?: string;
  label?: string;
  checkboxClassname?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  subText?: string;
}

const Checkbox = forwardRef<ElementRef<'input'>, CheckboxProps>(
  (
    {
      className,
      label,
      name,
      iconType = 'check',
      size = 'md',
      fill,
      checked,
      defaultChecked,
      checkboxClassname,
      disabled,
      subText,
      ...props
    },
    ref,
  ) => {
    return (
      <label
        htmlFor={name}
        className={cn(
          'cursor-pointer',
          'inline-flex items-center gap-[10px]',
          subText && 'items-start',
          'group relative',
          'text-sm leading-tight text-secondary',
          size === 'sm' && 'text-xs leading-5',
          size === 'md' && 'text-sm leading-5',
          className,
        )}
      >
        <input
          {...props}
          type="checkbox"
          name={name}
          ref={ref}
          className={cn(
            'h-[14px] w-[14px]',
            'peer',
            'cursor-pointer border border-primary-base opacity-0',
          )}
          checked={checked}
          disabled={disabled}
        />
        <span
          className={cn(
            'peer-focus:border-primary-base',
            checked || defaultChecked ? 'bg-fill-tertiary' : 'bg-fill-primary',
            'absolute h-4 w-4 cursor-pointer rounded border',
            fill
              ? 'group-hover:border-disabled'
              : 'group-hover:border-primary-base',
            'border-disabled',
            (checked || defaultChecked) && 'border-primary-base',
            disabled &&
              'border-disabled group-hover:border-disabled pointer-events-none',
            checkboxClassname,
          )}
        >
          {iconType === 'minus' ? (
            <BsDash
              className={cn(
                'text-sm',
                fill ?? 'text-primary-base',
                checked || defaultChecked ? 'block' : 'hidden',
                disabled && 'text-disabled',
              )}
            />
          ) : (
            <BsCheck
              className={cn(
                'text-sm',
                fill ?? 'text-primary-base',
                checked || defaultChecked ? 'block' : 'hidden',
                disabled && 'text-disabled',
              )}
            />
          )}
        </span>
        {label ||
          (subText && (
            <div className="">
              {label}
              {subText && (
                <div className="text-content text-xs leading-4">{subText}</div>
              )}
            </div>
          ))}
      </label>
    );
  },
);

export default Checkbox;

interface _PROPS extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  groupClassName?: string;
  children: ReactNode;
  groupLabel?: string;
  labelSubText?: string;
  subText?: string;
  required?: boolean;
  textType?: 'default' | 'danger' | 'success' | 'warning';
}
export const CheckboxGroup: FC<_PROPS> = ({
  className,
  children,
  groupLabel,
  required,
  labelSubText = '(Optional)',
  subText,
  groupClassName,
  ...props
}) => (
  <div className={cn(props.textType, className)} {...props}>
    <div className={cn('flex items-center', groupLabel && 'mb-[10px]')}>
      {groupLabel && (
        <div className={cn('text-sm leading-5', 'block flex-1 font-semibold')}>
          {groupLabel}
          {required ? (
            <span
              className={cn(
                'text-sm font-semibold leading-5 text-danger',
                'ml-1 inline-block text-xs leading-[18px]',
              )}
            >
              *
            </span>
          ) : (
            <span className="ml-1 inline-block text-xs leading-[18px]">
              {labelSubText}
            </span>
          )}
        </div>
      )}
    </div>
    <div className={cn('gap-4', groupClassName)} role="group">
      {Children.map(children, (child) =>
        isValidElement<PROPS>(child)
          ? cloneElement(child, { ...child.props })
          : null,
      )}
    </div>
    {subText && <div className="input-subtext">{subText}</div>}
  </div>
);
